/* eslint-disable max-len */
import React from 'react'
import EmojiPicker from 'emoji-picker-react'
import {
  HtmlEditor,
  Image,
  Inject,
  Link,
  QuickToolbar,
  RichTextEditorComponent,
  Toolbar,
  ToolbarSettingsModel,
} from '@syncfusion/ej2-react-richtexteditor'
import { sanitizeRichTextHtml } from '@utils/sanitize-html.util'
import type { AnchoredPopoverPosition } from './noteEditor.types'
import NoteEditorTemplateDropdown from './NoteEditorTemplateDropdown.component'

type NoteVariableOption = {
  text: string
}

type NoteEditorRichTextPaneProps = {
  richTextEditorContainerRef: React.RefObject<HTMLDivElement>
  isViewMode: boolean
  isBodyRowLoading: boolean
  noteContent: string
  resolvedContentForView: string | null
  viewDisplayContent: string
  rteKey: string
  toolbarRef: React.RefObject<RichTextEditorComponent>
  onRteChange: (args: { value: string }) => void
  toolbarSettings: ToolbarSettingsModel
  showHappyPopover: boolean
  happyPopoverPosition: AnchoredPopoverPosition | null
  happyPopoverRef: React.RefObject<HTMLDivElement>
  onEmojiSelect: (emoji: string) => void
  showMentionPopover: boolean
  mentionPopoverPosition: AnchoredPopoverPosition | null
  mentionPopoverRef: React.RefObject<HTMLDivElement>
  mentionOptions: string[]
  onMentionSelect: (name: string) => void
  showVariablePopover: boolean
  variablePopoverPosition: AnchoredPopoverPosition | null
  variablePopoverRef: React.RefObject<HTMLDivElement>
  noteVariableList: NoteVariableOption[]
  onVariableSelect: (variableText: string) => void
  templateDropdown: React.ComponentProps<typeof NoteEditorTemplateDropdown>
}

const popoverStyle = (
  position: AnchoredPopoverPosition,
): React.CSSProperties => ({
  position: 'fixed',
  top: position.top,
  left: position.left,
})

const NoteEditorRichTextPane = ({
  richTextEditorContainerRef,
  isViewMode,
  isBodyRowLoading,
  noteContent,
  resolvedContentForView,
  viewDisplayContent,
  rteKey,
  toolbarRef,
  onRteChange,
  toolbarSettings,
  showHappyPopover,
  happyPopoverPosition,
  happyPopoverRef,
  onEmojiSelect,
  showMentionPopover,
  mentionPopoverPosition,
  mentionPopoverRef,
  mentionOptions,
  onMentionSelect,
  showVariablePopover,
  variablePopoverPosition,
  variablePopoverRef,
  noteVariableList,
  onVariableSelect,
  templateDropdown,
}: NoteEditorRichTextPaneProps): JSX.Element => (
  <div className="rich-text-editor-container" ref={richTextEditorContainerRef}>
    {isViewMode && !isBodyRowLoading ? (
      <div className="note-view-content">
        {noteContent?.includes('data-blob') &&
        resolvedContentForView === null ? (
          <div
            className="note-view-content-placeholder"
            style={{ minHeight: 60 }}
            aria-hidden
          />
        ) : (
          <div
            dangerouslySetInnerHTML={{
              __html: sanitizeRichTextHtml(viewDisplayContent),
            }}
          />
        )}
      </div>
    ) : isBodyRowLoading ? null : (
      <RichTextEditorComponent
        key={rteKey}
        ref={toolbarRef}
        value={noteContent}
        change={onRteChange}
        height="100%"
        toolbarSettings={toolbarSettings}
      >
        <Inject services={[Toolbar, Link, Image, HtmlEditor, QuickToolbar]} />
      </RichTextEditorComponent>
    )}
    {showHappyPopover && happyPopoverPosition && (
      <div
        ref={happyPopoverRef}
        className="ai-toolbar-popover ai-toolbar-popover--emoji"
        style={popoverStyle(happyPopoverPosition)}
      >
        <EmojiPicker
          onEmojiClick={(emojiData) => onEmojiSelect(emojiData.emoji)}
        />
      </div>
    )}
    {showMentionPopover && mentionPopoverPosition && (
      <div
        ref={mentionPopoverRef}
        className="ai-toolbar-popover ai-toolbar-popover--mention"
        style={popoverStyle(mentionPopoverPosition)}
      >
        <div className="ai-toolbar-popover__header">Mention patient</div>
        <div className="ai-toolbar-popover__list">
          {mentionOptions.length > 0 ? (
            mentionOptions.map((option) => (
              <button
                key={option}
                type="button"
                className="ai-toolbar-popover__item"
                onClick={() => onMentionSelect(option)}
              >
                {option}
              </button>
            ))
          ) : (
            <div className="ai-toolbar-popover__empty">
              No patients available
            </div>
          )}
        </div>
      </div>
    )}
    {showVariablePopover && variablePopoverPosition && (
      <div
        ref={variablePopoverRef}
        className="ai-toolbar-popover ai-toolbar-popover--variable"
        style={popoverStyle(variablePopoverPosition)}
      >
        <div className="ai-toolbar-popover__header">Insert variable</div>
        <div className="ai-toolbar-popover__list">
          {noteVariableList.map((variable) => (
            <button
              key={variable.text}
              type="button"
              className="ai-toolbar-popover__item"
              onClick={() => onVariableSelect(variable.text)}
            >
              {variable.text}
            </button>
          ))}
        </div>
      </div>
    )}
    <NoteEditorTemplateDropdown {...templateDropdown} />
  </div>
)

export default NoteEditorRichTextPane
